// Keep in sync with App\Support\RoleCatalog on the backend
// (api/app/Support/RoleCatalog.php). This static map exists so any
// authenticated user's own role labels (account menu, linked-user tables)
// can render without an API call — the full catalog with
// descriptions/categories for the admin role picker comes from GET /roles
// instead (users.manage only), see components/RolePicker.tsx.
export const ROLE_LABELS: Record<string, string> = {
  system_admin: 'System Administrator',

  memberships_administrator: 'Memberships Administrator',
  memberships_operator: 'Memberships Operator',
  memberships_viewer: 'Memberships Viewer',
  resources_administrator: 'Resources Administrator',
  resources_operator: 'Resources Operator',
  resources_viewer: 'Resources Viewer',
  revenue_administrator: 'Revenue Administrator',
  revenue_operator: 'Revenue Operator',
  revenue_viewer: 'Revenue Viewer',
  procurement_administrator: 'Procurement Administrator',
  procurement_operator: 'Procurement Operator',
  procurement_viewer: 'Procurement Viewer',
  human_resources_administrator: 'Human Resources Administrator',
  human_resources_operator: 'Human Resources Operator',
  human_resources_viewer: 'Human Resources Viewer',
  investment_management_administrator: 'Investment Management Administrator',
  investment_management_operator: 'Investment Management Operator',
  investment_management_viewer: 'Investment Management Viewer',

  member: 'Member',
  supplier: 'Supplier',
  customer: 'Customer',
};

// Roughly senior-to-junior, used to pick which role to headline when a user
// holds several (account menu, compact tables). Not used for authorization.
const SENIORITY_ORDER = [
  'system_admin',
  'memberships_administrator', 'resources_administrator', 'revenue_administrator', 'procurement_administrator',
  'human_resources_administrator', 'investment_management_administrator',
  'memberships_operator', 'resources_operator', 'revenue_operator', 'procurement_operator',
  'human_resources_operator', 'investment_management_operator',
  'memberships_viewer', 'resources_viewer', 'revenue_viewer', 'procurement_viewer',
  'human_resources_viewer', 'investment_management_viewer',
  'supplier', 'customer', 'member',
];

export const ROLE_OPTIONS = Object.entries(ROLE_LABELS)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

// True if the user holds any of the given permission keys — mirrors the
// `permission:a|b|c` OR-matching used by the backend's route middleware
// (see routes/api.php), so UI gated with the same key list stays consistent
// with what the API allows.
export function hasAnyPermission(
  user: { permissions?: string[] } | null | undefined,
  keys: string[]
): boolean {
  const permissions = user?.permissions ?? [];
  return keys.some((key) => permissions.includes(key));
}

// A short label for a user holding possibly several roles — e.g. for the
// account menu or a linked-users table cell where space is tight.
export function primaryRoleLabel(roles: string[] | null | undefined): string {
  const list = roles ?? [];
  if (list.length === 0) return '—';
  const sorted = [...list].sort((a, b) => {
    const ai = SENIORITY_ORDER.indexOf(a);
    const bi = SENIORITY_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
  const headline = roleLabel(sorted[0]);
  return list.length > 1 ? `${headline} +${list.length - 1} more` : headline;
}
